import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button } from '../components';
import { FileText, Plus, Calendar, User, Bell, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';

interface Note {
  id: string;
  titel: string;
  notitie: string;
  contact_gehad_met?: string;
  aangemaakt_op: string;
  herinneren_mij_op?: string;
  employer_id?: string;
  employer_first_name?: string;
  employer_last_name?: string;
  dossier_id?: string;
  dossier_client_name?: string;
}

export const ProfessionalNotesPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const [showForm, setShowForm] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState<string>('');
  const queryClient = useQueryClient();

  // Get active employers
  const { data: employers } = useQuery({
    queryKey: ['active-employers'],
    queryFn: async () => {
      const response = await api.get('/professional/active-employers');
      return response.data.data || [];
    }
  });

  // Get dossiers for note form
  const { data: dossiers } = useQuery({
    queryKey: ['professional-dossiers'],
    queryFn: async () => {
      const response = await api.get('/professional/dossiers');
      return response.data.data || [];
    }
  });

  // Get notes
  const { data: notes, isLoading } = useQuery({
    queryKey: ['notes', selectedEmployer],
    queryFn: async () => {
      const params: any = {};
      if (selectedEmployer) params.employer_id = selectedEmployer;
      const response = await api.get('/professional/notes', { params });
      return response.data.data || [];
    }
  });

  const createNote = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/professional/notes', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('profNotes.createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      setShowForm(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('profNotes.createError'));
    }
  });

  const deleteNote = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/professional/notes/${id}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('profNotes.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('profNotes.deleteError'));
    }
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('profNotes.title')}</h1>
          <p className="mt-1 text-gray-600">{t('profNotes.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg"
            value={selectedEmployer}
            onChange={(e) => setSelectedEmployer(e.target.value)}
          >
            <option value="">{t('profNotes.allEmployers')}</option>
            {employers?.map((emp: any) => (
              <option key={emp.employer_user_id} value={emp.employer_user_id}>
                {emp.employer_first_name} {emp.employer_last_name}
              </option>
            ))}
          </select>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('profNotes.newNote')}
          </Button>
        </div>
      </div>

      {showForm && (
        <NoteForm
          employers={employers || []}
          dossiers={dossiers || []}
          onSubmit={(data) => createNote.mutate(data)}
          onCancel={() => setShowForm(false)}
        />
      )}

      {isLoading ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600">{t('profNotes.loading')}</p>
          </div>
        </Card>
      ) : notes && notes.length > 0 ? (
        <div className="space-y-4">
          {notes.map((note: Note) => (
            <Card key={note.id}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{note.titel}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(note.aangemaakt_op).toLocaleDateString('nl-NL')}
                      </div>
                      {note.employer_first_name && (
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          {note.employer_first_name} {note.employer_last_name}
                        </div>
                      )}
                      {note.dossier_client_name && (
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 mr-1" />
                          {t('profNotes.dossier')} {note.dossier_client_name}
                        </div>
                      )}
                      {note.contact_gehad_met && (
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          {t('profNotes.contact')} {note.contact_gehad_met}
                        </div>
                      )}
                      {note.herinneren_mij_op && (
                        <div className="flex items-center text-blue-600">
                          <Bell className="w-4 h-4 mr-1" />
                          {t('profNotes.remind')} {new Date(note.herinneren_mij_op).toLocaleDateString('nl-NL')}
                        </div>
                      )}
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{note.notitie}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteNote.mutate(note.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="text-center py-12">
            <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('profNotes.noNotesTitle')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('profNotes.noNotesDesc')}
            </p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('profNotes.firstNote')}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

const NoteForm: React.FC<{
  employers: any[];
  dossiers: any[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ employers, dossiers, onSubmit, onCancel }) => {
  const { t } = useTranslation('pages');
  const [formData, setFormData] = useState({
    employer_id: '',
    dossier_id: '',
    titel: '',
    notitie: '',
    contact_gehad_met: '',
    herinneren_mij_op: ''
  });

  const filteredDossiers = dossiers.filter((d: any) => d.employer_id === formData.employer_id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      employer_id: formData.employer_id || undefined,
      dossier_id: formData.dossier_id || undefined,
      contact_gehad_met: formData.contact_gehad_met || undefined,
      herinneren_mij_op: formData.herinneren_mij_op || undefined
    });
  };

  return (
    <Card className="mb-6">
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('profNotes.formTitle')}</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('profNotes.employerLabel')}
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.employer_id}
            onChange={(e) => setFormData({ ...formData, employer_id: e.target.value, dossier_id: '' })}
          >
            <option value="">{t('profNotes.noEmployer')}</option>
            {employers.map((emp: any) => (
              <option key={emp.employer_user_id} value={emp.employer_user_id}>
                {emp.employer_first_name} {emp.employer_last_name}
              </option>
            ))}
          </select>
        </div>

        {formData.employer_id && filteredDossiers.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('profNotes.dossierLabel')}
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.dossier_id}
              onChange={(e) => setFormData({ ...formData, dossier_id: e.target.value })}
            >
              <option value="">{t('profNotes.noDossier')}</option>
              {filteredDossiers.map((d: any) => (
                <option key={d.id} value={d.id}>{d.client_name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('profNotes.titleLabel')} *
          </label>
          <input
            type="text"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.titel}
            onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
            placeholder={t('profNotes.titlePlaceholder')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('profNotes.noteLabel')} *
          </label>
          <textarea
            required
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.notitie}
            onChange={(e) => setFormData({ ...formData, notitie: e.target.value })}
            placeholder={t('profNotes.notePlaceholder')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('profNotes.contactLabel')}
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.contact_gehad_met}
              onChange={(e) => setFormData({ ...formData, contact_gehad_met: e.target.value })}
              placeholder={t('profNotes.contactPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('profNotes.remindLabel')}
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.herinneren_mij_op}
              onChange={(e) => setFormData({ ...formData, herinneren_mij_op: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('profNotes.cancel')}
          </Button>
          <Button type="submit">
            {t('profNotes.create')}
          </Button>
        </div>
      </form>
    </Card>
  );
};
